import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import api from "../utils/api";

// Backend decides the status string - never recompute it here.
const STATUS_COLORS = {
  "Within Range": "#16a34a",
  Caution: "#f59e0b",
  "Out of Range": "#dc2626",
};
const STATUS_ICONS = {
  "Within Range": "✔",
  Caution: "⚠",
  "Out of Range": "✕",
};

const STANDARD_COLOR = "#22a06b";

// Table styling mirrors the Live table (channelStyles in LiveTab.js).
const tableStyle = { width: "100%", borderCollapse: "collapse", fontSize: "14px" };
const thStyle = {
  padding: "12px 8px",
  textAlign: "center",
  borderBottom: "2px solid #dee2e6",
  backgroundColor: "#f8f9fa",
  fontWeight: 600,
  whiteSpace: "nowrap",
};
const tdStyle = {
  padding: "10px 8px",
  textAlign: "center",
  borderBottom: "1px solid #dee2e6",
};

const fmt = (v, digits = 1) =>
  v === null || v === undefined || Number.isNaN(Number(v))
    ? "-"
    : Number(v).toFixed(digits);

// Deviation is signed: show it with an explicit +/- like the QCP sheet does.
const fmtDeviation = (v) => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return "-";
  const n = Number(v);
  return `${n > 0 ? "+" : ""}${n.toFixed(1)}`;
};

const prettyDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = String(iso).split("-");
  return y && m && d ? `${d}-${m}-${y}` : iso;
};

const LegendSwatch = ({ color, label }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
    <span
      style={{
        width: 11,
        height: 11,
        borderRadius: 3,
        background: color,
        display: "inline-block",
      }}
    />
    {label}
  </span>
);

const QcpSummary = ({ username, startDate, endDate }) => {
  const [rows, setRows] = useState([]);
  const [period, setPeriod] = useState({ from: "", to: "" });
  const [loading, setLoading] = useState(false);

  const fetchSummary = useCallback(async () => {
    if (!username) return;
    setLoading(true);
    try {
      // start_date / end_date match the other Historical endpoints. actual_temp,
      // deviation and status are all averaged over this range by the backend.
      const params = { username };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await api.get("/qcp-summary", { params });
      if (res.data && res.data.status === "success") {
        setRows(res.data.data || []);
        setPeriod({
          from: res.data.from || res.data.date || startDate || "",
          to: res.data.to || res.data.date || endDate || "",
        });
      } else {
        setRows([]);
      }
    } catch (err) {
      console.error("Fetch qcp-summary error:", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [username, startDate, endDate]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const chart = useMemo(() => {
    const categories = rows.map((r) => r.location || r.qcp_no || "");
    return {
      series: [
        {
          name: "Standard (°C)",
          data: rows.map((r) => ({
            x: r.location || r.qcp_no,
            y: r.standard_temp,
            fillColor: STANDARD_COLOR,
          })),
        },
        {
          name: "Actual Temp (°C)",
          // Each Actual bar is tinted by the status the backend returned.
          data: rows.map((r) => ({
            x: r.location || r.qcp_no,
            y: r.actual_temp,
            fillColor: STATUS_COLORS[r.status] || "#94a3b8",
          })),
        },
      ],
      options: {
        chart: {
          type: "bar",
          toolbar: { show: false },
          fontFamily: "inherit",
          animations: { enabled: false },
        },
        plotOptions: {
          bar: { columnWidth: "60%", dataLabels: { position: "top" } },
        },
        dataLabels: {
          enabled: true,
          offsetY: -18,
          style: { fontSize: "11px", fontWeight: 700, colors: ["#334155"] },
          formatter: (val) => (val === null ? "" : Number(val).toFixed(1)),
        },
        stroke: { show: true, width: 2, colors: ["transparent"] },
        xaxis: {
          categories,
          title: { text: "Quality Control Point", style: { fontSize: "12px" } },
          labels: { style: { fontSize: "11px" } },
        },
        yaxis: {
          title: { text: "Temperature (°C)", style: { fontSize: "12px" } },
          labels: { formatter: (v) => Number(v).toFixed(0) },
        },
        legend: { show: false },
        grid: { borderColor: "#e2e8f0" },
        tooltip: { y: { formatter: (v) => (v === null ? "-" : `${Number(v).toFixed(1)} °C`) } },
      },
    };
  }, [rows]);

  // Empty data -> hide the whole section (sensors without limits configured).
  if (loading && rows.length === 0) return null;
  if (!rows.length) return null;

  return (
    <div>
      <div
        style={{
          background: "#1f5c99",
          color: "#fff",
          fontWeight: 700,
          fontSize: 13,
          padding: "8px 12px",
          borderRadius: "8px 8px 0 0",
        }}
      >
        Baseline QCP Data
      </div>

      <div style={{ overflowX: "auto", border: "1px solid #dee2e6", borderTop: "none" }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>QCP No</th>
              <th style={{ ...thStyle, textAlign: "left" }}>Location</th>
              <th style={thStyle}>Standard (°C)</th>
              <th style={thStyle}>Actual Temp (°C)</th>
              <th style={thStyle}>UCL</th>
              <th style={thStyle}>LCL</th>
              <th style={thStyle}>Deviation (°C)</th>
              <th style={thStyle}>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => {
              const statusColor = STATUS_COLORS[r.status] || "#64748b";
              return (
                <tr
                  key={r.meter_id || `${r.qcp_no}-${idx}`}
                  style={{ backgroundColor: idx % 2 === 1 ? "#f8f9fa" : "#fff" }}
                >
                  <td style={{ ...tdStyle, fontWeight: 700 }}>{r.qcp_no || "-"}</td>
                  <td style={{ ...tdStyle, textAlign: "left" }}>{r.location || "-"}</td>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{fmt(r.standard_temp)}</td>
                  <td style={{ ...tdStyle, fontWeight: 700, color: statusColor }}>
                    {fmt(r.actual_temp)}
                  </td>
                  <td style={{ ...tdStyle, color: "#16a34a" }}>{fmt(r.ucl_temp)}</td>
                  <td style={{ ...tdStyle, color: "#dc2626" }}>{fmt(r.lcl_temp)}</td>
                  <td style={tdStyle}>{fmtDeviation(r.deviation)}</td>
                  <td style={{ ...tdStyle, color: statusColor, fontWeight: 700, whiteSpace: "nowrap" }}>
                    <span style={{ marginRight: 6 }}>{STATUS_ICONS[r.status] || ""}</span>
                    {r.status || "-"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div
        style={{
          border: "1px solid #dee2e6",
          borderTop: "none",
          borderRadius: "0 0 8px 8px",
          padding: "16px 8px 8px",
          background: "#fff",
        }}
      >
        <div
          style={{
            textAlign: "center",
            fontWeight: 700,
            fontSize: 15,
            color: "#0f172a",
            marginBottom: 8,
          }}
        >
          QCP Temperatures: Standard vs Actual (
          {period.from && period.to && period.from !== period.to
            ? `${prettyDate(period.from)} to ${prettyDate(period.to)}`
            : prettyDate(period.from || period.to)}
          )
        </div>
        <ReactApexChart
          options={chart.options}
          series={chart.series}
          type="bar"
          height={420}
        />

        {/* Actual bars are coloured per status, so the legend explains the statuses
            rather than pretending the series has one colour. */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 18,
            paddingTop: 4,
            fontSize: 12,
            color: "#475569",
          }}
        >
          <LegendSwatch color={STANDARD_COLOR} label="Standard (°C)" />
          <span style={{ color: "#94a3b8" }}>|</span>
          <span style={{ fontWeight: 600 }}>Actual Temp (°C):</span>
          {Object.keys(STATUS_COLORS).map((k) => (
            <LegendSwatch key={k} color={STATUS_COLORS[k]} label={k} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default QcpSummary;
